import { useCallback, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { PiArrowLeft, PiPlugLight } from 'react-icons/pi';
import { IntegrationProviderId } from 'src/api/generated';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { CardSkeleton } from 'src/modules/shared/components/atoms/cards/card-skeleton';
import { TextSkeleton } from 'src/modules/shared/components/atoms/text/text-skeleton';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { useIntegrations } from 'src/modules/shared/hooks/api/integrations/integrations';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { PanelHeader } from 'src/modules/shared/components/molecules/panel/panel-header';
import { IntegrationPermission } from 'src/modules/shared/types/permissions.type';
import { IntegrationConnectionBadges } from '../molecules/integrations/integration-connection-badges';
import { IntegrationBrandAvatar } from '../molecules/integrations/integration-brand-avatar';
import { IntegrationIndexCard } from '../molecules/integrations/integration-index-card';
import { MeliIntegrationCard } from '../organisms/integrations/meli-integration-card';
import { TwoClicsIntegrationCard } from '../organisms/integrations/twoclics-integration-card';
import { KitePropIntegrationCard } from '../organisms/integrations/kiteprop-integration-card';
import { TopPropiedadesIntegrationCard } from '../organisms/integrations/toppropiedades-integration-card';
import { WasiIntegrationCard } from '../organisms/integrations/wasi-integration-card';
import { WhatsAppAccountsCard } from '../organisms/integrations/whatsapp-accounts-card';
import { WhatsAppIntegrationCard } from '../organisms/integrations/whatsapp-integration-card';
import { Route as SettingsIntegrationsIndexRoute } from 'src/routes/settings/integrations.index';
import { Route as SettingsIntegrationsDetailRoute } from 'src/routes/settings/integrations.$integrationId';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';
import type { IntegrationView } from '../../schemas/integrations.schema';

type IntegrationSection = 'messaging' | 'real-estate';

interface IntegrationCatalogEntry {
  id: IntegrationView;
  section: IntegrationSection;
  title: string;
  description: string;
  icon: React.ReactNode;
  providerId?: IntegrationProviderId;
}

interface IntegrationsSettingsPageProps {
  activeIntegration?: IntegrationView;
}

const INTEGRATION_SECTIONS: Record<
  IntegrationSection,
  { titleKey: TranslationKey }
> = {
  messaging: { titleKey: 'settings.integrations.sidebar.messaging' },
  'real-estate': { titleKey: 'settings.integrations.sidebar.realEstate' },
};

const IntegrationsIndexSkeleton = () => {
  return (
    <div className="space-y-8">
      <div className="space-y-2">
        <TextSkeleton className="h-4 w-80" />
        <TextSkeleton className="h-4 w-64" />
      </div>
      {Array.from({ length: 2 }).map((_, sectionIndex) => (
        <div key={sectionIndex} className="space-y-3">
          <TextSkeleton className="h-6 w-40" />
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {Array.from({ length: sectionIndex === 0 ? 1 : 3 }).map(
              (_, cardIndex) => (
                <CardSkeleton
                  key={`${sectionIndex}-${cardIndex}`}
                  className="min-h-[128px]"
                  header={{ avatar: true, title: true, subtitle: true }}
                  content={{ lines: 2, widths: ['w-full', 'w-3/4'] }}
                />
              )
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const IntegrationDetailSkeleton = () => {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <TextSkeleton className="h-7 w-56" />
        <TextSkeleton className="h-4 w-72" />
      </div>

      <CardSkeleton
        className="min-h-[240px]"
        header={{ avatar: true, title: true, subtitle: true }}
        content={{ lines: 4, widths: ['w-full', 'w-5/6', 'w-full', 'w-2/3'] }}
      />
    </div>
  );
};

export const IntegrationsSettingsPage: React.FC<
  IntegrationsSettingsPageProps
> = ({ activeIntegration }) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { hasPermission: canRead } = useHasPermissions(
    IntegrationPermission.Read
  );
  const { integrations, loading: loadingIntegrations } =
    useIntegrations(canRead);
  const isPageLoading = canRead && loadingIntegrations && !integrations;

  const connectionByProviderId = useMemo(() => {
    const entries =
      integrations?.items.map(
        (item) => [item.providerId, item.connection] as const
      ) ?? [];
    return new Map(entries);
  }, [integrations]);

  const integrationCatalog = useMemo<IntegrationCatalogEntry[]>(
    () => [
      {
        id: 'whatsapp',
        section: 'messaging',
        title: t('settings.integrations.whatsapp.title'),
        description: t('settings.integrations.whatsapp.description'),
        icon: <IntegrationBrandAvatar integrationId="whatsapp" />,
      },
      {
        id: 'meli',
        section: 'real-estate',
        title: t('settings.integrations.meli.title'),
        description: t('settings.integrations.meli.subtitle'),
        icon: <IntegrationBrandAvatar integrationId="meli" />,
        providerId: IntegrationProviderId.MELI,
      },
      {
        id: '2clics',
        section: 'real-estate',
        title: t('settings.integrations.2clics.title'),
        description: t('settings.integrations.2clics.subtitle'),
        icon: <IntegrationBrandAvatar integrationId="2clics" />,
        providerId: '2clics' as IntegrationProviderId,
      },
      {
        id: 'wasi',
        section: 'real-estate',
        title: t('settings.integrations.wasi.title'),
        description: t('settings.integrations.wasi.subtitle'),
        icon: <IntegrationBrandAvatar integrationId="wasi" />,
        providerId: IntegrationProviderId.WASI,
      },
      {
        id: 'toppropiedades',
        section: 'real-estate',
        title: t('settings.integrations.toppropiedades.title'),
        description: t('settings.integrations.toppropiedades.subtitle'),
        icon: <IntegrationBrandAvatar integrationId="toppropiedades" />,
        providerId: IntegrationProviderId.TOPPROPIEDADES,
      },
      {
        id: 'kiteprop',
        section: 'real-estate',
        title: t('settings.integrations.kiteprop.title'),
        description: t('settings.integrations.kiteprop.subtitle'),
        icon: <IntegrationBrandAvatar integrationId="kiteprop" />,
        providerId: 'kiteprop' as IntegrationProviderId,
      },
    ],
    [t]
  );

  const openIntegration = useCallback(
    (integrationId: IntegrationView) => {
      void navigate({
        to: SettingsIntegrationsDetailRoute.to,
        params: { integrationId },
        search: (prev) => ({ phoneVersion: prev.phoneVersion ?? 'v1' }),
      });
    },
    [navigate]
  );

  const goToIndex = useCallback(() => {
    void navigate({
      to: SettingsIntegrationsIndexRoute.to,
      search: (prev) => ({ phoneVersion: prev.phoneVersion ?? 'v1' }),
    });
  }, [navigate]);

  const groupedIntegrations = useMemo(() => {
    return (Object.keys(INTEGRATION_SECTIONS) as IntegrationSection[]).map(
      (section) => ({
        section,
        items: integrationCatalog.filter(
          (integration) => integration.section === section
        ),
      })
    );
  }, [integrationCatalog]);

  const renderDetail = () => {
    if (activeIntegration === 'whatsapp') {
      return (
        <div className="space-y-6">
          <WhatsAppIntegrationCard />
          <WhatsAppAccountsCard />
        </div>
      );
    }

    if (activeIntegration === 'meli') {
      return <MeliIntegrationCard />;
    }

    if (activeIntegration === 'wasi') {
      return <WasiIntegrationCard />;
    }

    if (activeIntegration === '2clics') {
      return <TwoClicsIntegrationCard />;
    }

    if (activeIntegration === 'toppropiedades') {
      return <TopPropiedadesIntegrationCard />;
    }

    if (activeIntegration === 'kiteprop') {
      return <KitePropIntegrationCard />;
    }

    return null;
  };

  return (
    <div className="flex flex-col h-full">
      <Panel className="h-full flex flex-col">
        <PanelHeader className="px-4 py-3 border-b">
          <div className="flex items-center gap-3">
            {activeIntegration ? (
              <Button
                variant="neutral"
                appearance="ghost"
                size="sm"
                isIconOnly
                aria-label={t('settings.integrations.index.back')}
                onClick={goToIndex}
              >
                <PiArrowLeft className="w-5 h-5" />
              </Button>
            ) : (
              <PiPlugLight className="w-8 h-8" />
            )}
            <Text as="h2" size="h2" weight="medium">
              {t('settings.integrations.title')}
            </Text>
          </div>
        </PanelHeader>

        <div className="flex-1 overflow-auto p-4">
          {isPageLoading ? (
            activeIntegration ? (
              <IntegrationDetailSkeleton />
            ) : (
              <IntegrationsIndexSkeleton />
            )
          ) : activeIntegration ? (
            renderDetail()
          ) : (
            <div className="space-y-8">
              <div className="space-y-2">
                <Text size="paragraph" color="secondary">
                  {t('settings.integrations.index.description')}
                </Text>
              </div>

              {groupedIntegrations.map(({ section, items }) => (
                <div key={section} className="space-y-3">
                  <Text size="h6" weight="medium">
                    {t(INTEGRATION_SECTIONS[section].titleKey)}
                  </Text>

                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    {items.map((integration) => {
                      const connection = integration.providerId
                        ? connectionByProviderId.get(integration.providerId) ??
                          null
                        : null;
                      const badges =
                        integration.providerId && !loadingIntegrations ? (
                          <IntegrationConnectionBadges
                            status={connection?.status ?? 'disconnected'}
                            healthStatus={connection?.healthStatus ?? null}
                            t={t}
                          />
                        ) : null;

                      return (
                        <IntegrationIndexCard
                          key={integration.id}
                          title={integration.title}
                          description={integration.description}
                          icon={integration.icon}
                          badges={badges}
                          onClick={() => openIntegration(integration.id)}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Panel>
    </div>
  );
};
